import { Icon } from "@iconify/react";
import React from "react";
import { NavLink } from "react-router-dom";
import AuthUser from "../../Hooks/authUser";
import logo from "../../Assets/logo.png";

const NAV_ITEMS = [
  {
    to: "overview",
    icon: "solar:widget-5-linear",
    activeIcon: "solar:widget-5-bold",
    label: "Overview",
    description: "Therapy summary",
  },
  {
    to: "reports",
    icon: "solar:chart-2-linear",
    activeIcon: "solar:chart-2-bold",
    label: "Reports",
    description: "Progress & analytics",
  },
  {
    to: "settings",
    icon: "solar:tuning-2-linear",
    activeIcon: "solar:tuning-2-bold",
    label: "Settings",
    description: "Lens calibration",
  },
  {
    to: "faq",
    icon: "solar:chat-round-dots-linear",
    activeIcon: "solar:chat-round-dots-bold",
    label: "Help & FAQ",
    description: "Support center",
  },
];

const UserDashboardNav = ({ onClose }) => {
  const { userInfo, logout } = AuthUser();

  const initials = userInfo?.name
    ? userInfo.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "P";

  return (
    <aside
      className="flex flex-col w-[270px] h-full select-none overflow-hidden"
      style={{
        background: "linear-gradient(160deg, #0d1117 0%, #0f1623 50%, #0d1117 100%)",
      }}
    >
      {/* Top glow accent */}
      <div
        className="absolute top-0 left-0 right-0 h-px opacity-30"
        style={{
          background: "linear-gradient(90deg, transparent, #6366f1, #22d3ee, transparent)",
        }}
      />

      {/* ── Brand ── */}
      <div className="flex items-center justify-center px-5 pt-6 pb-5">
        <a href="/" >
          <div className="relative">
            <div className="absolute inset-0 rounded-xl bg-indigo-500/20 blur-md group-hover:bg-indigo-500/30 transition-all duration-300" />
            <img
              src={logo}
              alt="CareSee"
              className="relative h-9 w-auto object-contain brightness-110"
            />
          </div>
          
        </a>

        {/* Mobile close */}
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all"
          >
            <Icon icon="lucide:x" className="text-lg" />
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="mx-5 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent mb-5" />

      {/* ── Section label ── */}
      <p className="px-5 mb-2 text-[9px] font-bold tracking-[0.18em] text-slate-600 uppercase">
        Navigation
      </p>

      {/* ── Nav links ── */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className="block rounded-xl overflow-hidden"
          >
            {({ isActive }) => (
              <div
                className={`relative flex items-center gap-3.5 px-3.5 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? "text-white"
                    : "text-slate-500 hover:text-slate-200 hover:bg-white/[0.04]"
                }`}
                style={
                  isActive
                    ? {
                        background:
                          "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(79,70,229,0.15) 100%)",
                        boxShadow: "inset 0 0 0 1px rgba(99,102,241,0.25), 0 4px 20px rgba(99,102,241,0.1)",
                      }
                    : {}
                }
              >
                {/* Active left bar */}
                {isActive && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full"
                    style={{
                      background: "linear-gradient(180deg, #818cf8, #22d3ee)",
                      boxShadow: "0 0 10px rgba(129,140,248,0.6)",
                    }}
                  />
                )}

                {/* Icon container */}
                <div
                  className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    isActive ? "bg-indigo-500/20" : "bg-white/[0.03] group-hover:bg-white/[0.06]"
                  }`}
                >
                  <Icon
                    icon={isActive ? item.activeIcon : item.icon}
                    className={`text-[18px] transition-all duration-200 ${
                      isActive
                        ? "text-indigo-300"
                        : "text-slate-500"
                    }`}
                  />
                </div>

                {/* Label + description */}
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] font-semibold leading-none mb-0.5 ${isActive ? "text-white" : "text-slate-400"}`}>
                    {item.label}
                  </p>
                  <p className={`text-[10px] font-medium truncate ${isActive ? "text-indigo-300/70" : "text-slate-600"}`}>
                    {item.description}
                  </p>
                </div>

                {/* Active arrow indicator */}
                {isActive && (
                  <Icon icon="solar:arrow-right-bold" className="text-indigo-400/60 text-xs flex-shrink-0" />
                )}
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Divider */}
      <div className="mx-5 mt-4 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />

      {/* ── User profile card ── */}
      <div className="p-4 space-y-3">
        <div
          className="flex items-center gap-3 p-3 rounded-xl"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {/* Avatar with gradient ring */}
          <div className="relative flex-shrink-0">
            <div
              className="absolute inset-0 rounded-full blur-sm opacity-60"
              style={{ background: "linear-gradient(135deg, #6366f1, #22d3ee)" }}
            />
            <div
              className="relative w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-black"
              style={{ background: "linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)" }}
            >
              {initials}
            </div>
            {/* Online dot */}
            <span
              className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
              style={{
                background: "#22c55e",
                borderColor: "#0d1117",
                boxShadow: "0 0 6px rgba(34,197,94,0.6)",
              }}
            />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-white truncate leading-tight">
              {userInfo?.name || "Patient"}
            </p>
            <p className="text-[10px] font-semibold text-slate-500 truncate mt-0.5">
              {userInfo?.email || "patient@caresee.com"}
            </p>
          </div>

          {/* Settings shortcut */}
          <NavLink
            to="settings"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-300 hover:bg-white/[0.05] transition-all"
          >
            <Icon icon="solar:settings-linear" className="text-sm" />
          </NavLink>
        </div>

        {/* Sign out */}
        <button
          onClick={logout}
          className="group w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-bold text-slate-600 hover:text-rose-400 transition-all duration-200"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.border = "1px solid rgba(244,63,94,0.2)";
            e.currentTarget.style.background = "rgba(244,63,94,0.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.border = "1px solid rgba(255,255,255,0.05)";
            e.currentTarget.style.background = "rgba(255,255,255,0.02)";
          }}
        >
          <Icon icon="solar:logout-2-linear" className="text-sm" />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

export default UserDashboardNav;
